/**
 * Sidebar navigation config.
 * Structure supports: single links, expandable groups, and role-based visibility.
 * Add pathMatch arrays so the correct menu stays open when on a route.
 *
 * Item types:
 * - link: { to, label, icon?, roles? } — roles: ['admin'] = show only for admin
 * - group: { id, label, icon?, pathMatch?, children } — children are sections or links
 * - section: { label, pathMatch?, items } — items are links (for sub-menus under a group)
 */

const ICONS = {
  dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a4 4 0 014-4h9m-9 0a4 4 0 014-4h12a4 4 0 014 4v2M7 9a4 4 0 104 0 4 4 0 00-4 0z',
  'user-add': 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  dialer: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  emails: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  chevron: 'M9 5l7 7-7 7',
};

/**
 * Sidebar menu items.
 * - Top-level can be: link | group
 * - group.children can be: section { label, pathMatch?, items } or link { to, label, roles? }
 */
export const sidebarMenu = [
  {
    type: 'link',
    to: '/dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    pathMatch: ['/dashboard'],
    roles: ['admin', 'manager', 'team_leader'],
  },
  {
    type: 'link',
    to: '/my-dashboard',
    label: 'My dashboard',
    icon: 'dashboard',
    roles: ['agent'],
  },
  {
    type: 'group',
    id: 'admin-users',
    label: 'Users',
    icon: 'users',
    permissionsAny: ['user.list', 'user.create'],
    pathMatch: ['/admin/users', '/create-user'],
    children: [
      {
        type: 'section',
        label: null,
        pathMatch: ['/admin/users', '/create-user'],
        items: [
          { to: '/admin/users', label: 'User list', permission: 'user.list' },
          { to: '/create-user', label: 'Create user', permission: 'user.create' },
        ],
      },
    ],
  },
  {
    type: 'group',
    id: 'emails',
    label: 'Emails',
    icon: 'emails',
    pathMatch: ['/emails', '/emails/tickets/reports', '/admin/smtp-settings', '/admin/ticket-categories', '/admin/template-settings'],
    children: [
      {
        type: 'section',
        label: null,
        pathMatch: [
          '/emails/inbox',
          '/emails/tickets',
          '/emails/my-tickets',
          '/emails/my-completed',
          '/emails/tickets/unassign',
          '/emails/tickets/junk',
          '/emails/tickets/merged',
          '/emails/tickets/assigned',
          '/emails/tickets/completed',
          '/emails/csat-emails',
          '/emails/tickets/create',
          '/emails/tickets/reports',
          '/emails/compose',
          '/emails/mailboxes',
          '/admin/smtp-settings',
          '/admin/ticket-categories',
          '/admin/template-settings',
        ],
        items: [
          { to: '/emails/my-tickets', label: 'My Assigned Tickets', permission: 'ticket.list.own' },
          { to: '/emails/my-completed', label: 'Completed by me', permission: 'ticket.list.own' },
          { to: '/emails/tickets', label: 'Tickets' },
          {
            to: '/emails/tickets/reports',
            label: 'Ticket Reports',
            permission: 'ticket.reports.customized',
          },
          { to: '/emails/compose', label: 'Compose email', permission: 'ticket.email.compose' },
          { to: '/emails/tickets/unassign', label: 'Unassigned Tickets', permission: 'ticket.list.unassigned' },
          { to: '/emails/tickets/assigned', label: 'All Assigned Tickets', roles: ['admin', 'manager', 'team_leader'] },
          { to: '/emails/tickets/completed', label: 'All Completed Tickets', roles: ['admin', 'manager', 'team_leader'] },
          { to: '/emails/csat-emails', label: 'CSAT Emails', permission: 'ticket.csat_emails.view' },
          { to: '/emails/tickets/junk', label: 'Junk Tickets', permission: 'ticket.list.junk' },
          { to: '/emails/tickets/merged', label: 'Merged tickets', permission: 'ticket.list.merged' },
          {
            to: '/admin/template-settings',
            label: 'Configuration settings',
            permission: 'ticket.settings.view',
          },
        ],
      },
    ],
  },
];

export { ICONS };

/**
 * Check if current path should expand a group/section.
 * @param {string} currentPath
 * @param {string[]|undefined} pathMatch
 */
export function pathMatches(currentPath, pathMatch) {
  if (!pathMatch || !Array.isArray(pathMatch)) return false;
  return pathMatch.some((p) => currentPath === p || currentPath.startsWith(p + '/'));
}

/**
 * Filter item by user role. If item has no roles, show to all. If roles array, show only if user role is in it.
 * @param {{ roles?: string[] }} item
 * @param {boolean} isAdmin
 * @param {string} [userRole] - current user's role (e.g. 'admin', 'manager', 'agent')
 */
export function canShowByRole(item, isAdmin, userRole) {
  if (!item.roles || item.roles.length === 0) return true;
  if (userRole && item.roles.includes(userRole)) return true;
  if (isAdmin && item.roles.includes('admin')) return true;
  return false;
}

/**
 * Ticket/User menu items: prefer `permission` or `permissionsAny` (see ../config/permissions.js).
 * @param {{ permission?: string, permissionsAny?: string[], roles?: string[] }} item
 * @param {(key: string) => boolean} hasPermission
 * @param {boolean} isAdmin
 * @param {string|null|undefined} userRole
 */
export function canShowMenuItem(item, hasPermission, isAdmin, userRole) {
  if (item.permission) return hasPermission(item.permission);
  if (item.permissionsAny && item.permissionsAny.length) {
    return item.permissionsAny.some((k) => hasPermission(k));
  }
  return canShowByRole(item, isAdmin, userRole);
}
